import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Presentation.css";

const API = process.env.REACT_APP_BACKEND_URL;

function MissionControlSlide() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/public/stats`)
      .then((r) => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);
  const tiles = [
    ["Total Generations", stats ? stats.generations_total.toLocaleString() : "5,600+", "#ccff00"],
    ["AI Voice Generations", stats ? stats.voice_generations.toLocaleString() : "5,000+", "#06b6d4"],
    ["Registered Users", stats ? stats.users_total.toLocaleString() : "—", "#ff0066"],
    ["Active Subscriptions", stats ? stats.subscriptions_active.toLocaleString() : "35", "#7c3aed"],
    ["Marketplace DNAs", stats ? stats.marketplace_dnas.toLocaleString() : "700+", "#06b6d4"],
    ["Prompt Library", stats ? stats.prompt_library.toLocaleString() : "70,866", "#ccff00"],
  ];
  return (
    <>
      <div className="deck-glow-orb" style={{ width: 450, height: 450, background: "#06b6d4", top: "-20%", right: "-8%", opacity: 0.15 }} />
      <div className="deck-kicker">08 / Live Metrics</div>
      <h2 className="deck-h2">
        Mission Control: <span className="deck-volt-text">these numbers are live</span>
      </h2>
      <p className="deck-sub" style={{ marginBottom: "2rem" }}>
        Pulled directly from the production database — right now, as you read this slide.
      </p>
      <div className="deck-card" style={{ maxWidth: 980, padding: "1.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1.25rem" }}>
          <span className="deck-live-dot" />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem", letterSpacing: "0.2em", color: "#ccff00", textTransform: "uppercase" }}>
            Live from MANNIS Mission Control
          </span>
        </div>
        <div className="deck-grid-3" style={{ gap: "1rem" }}>
          {tiles.map(([label, value, accent]) => (
            <div key={label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "1.1rem 1.25rem", borderTop: `3px solid ${accent}` }}>
              <div className="deck-stat-number" style={{ fontSize: "1.9rem", color: accent }}>{value}</div>
              <div className="deck-stat-label">{label}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: "1.5rem" }}>
          <a
            className="deck-cta-btn"
            style={{ fontSize: "0.9rem", padding: "0.75rem 1.8rem" }}
            href={`${window.location.origin}/admin`}
            target="_blank"
            rel="noreferrer"
            data-testid="deck-open-dashboard-btn"
          >
            Open Live Dashboard →
          </a>
        </div>
      </div>
    </>
  );
}

const SLIDES = [
  {
    id: "title",
    render: () => (
      <>
        <div className="deck-glow-orb" style={{ width: 500, height: 500, background: "#7c3aed", top: "-15%", right: "-10%" }} />
        <div className="deck-glow-orb" style={{ width: 400, height: 400, background: "#06b6d4", bottom: "-20%", left: "-8%" }} />
        <div className="deck-kicker">Investor Presentation — 2026</div>
        <h1 className="deck-h1">
          SOUND <span className="deck-gradient-text">DNA</span> PRO
        </h1>
        <p className="deck-sub" style={{ fontSize: "1.4rem" }}>
          The AI production companion for the generative music era.
          <br />
          Built for creators on <strong style={{ color: "#f4f4f5" }}>Suno</strong>, <strong style={{ color: "#f4f4f5" }}>Udio</strong> &amp; every AI music platform.
        </p>
        <div style={{ marginTop: "3rem" }}>
          <a
            href="https://sounddna.pro"
            target="_blank"
            rel="noreferrer"
            data-testid="deck-title-site-link"
            style={{ fontFamily: "'JetBrains Mono', monospace", color: "#ccff00", fontSize: "1rem", textDecoration: "underline", textUnderlineOffset: "5px" }}
          >
            sounddna.pro →
          </a>
        </div>
      </>
    ),
  },
  {
    id: "problem",
    render: () => (
      <>
        <div className="deck-kicker">01 / The Problem</div>
        <h2 className="deck-h2">
          AI music tools generate songs.<br />
          <span className="deck-pink-text">They don't make you a producer.</span>
        </h2>
        <div style={{ maxWidth: 860 }}>
          {[
            ["Prompt roulette", "Suno & Udio output is only as good as the style prompt. Most creators guess — and burn credits on mediocre results."],
            ["No production layer", "Generators give you a finished MP3. Creators need stems, MIDI, vocals, and lyrics they can actually work with."],
            ["Fragmented workflow", "Lyrics in one app, stems in another, voice tools in a third. There is no home base for the AI music creator."],
          ].map(([title, body], i) => (
            <div className="deck-problem-item" key={i}>
              <div className="deck-problem-num">0{i + 1}</div>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "product",
    render: () => (
      <>
        <div className="deck-glow-orb" style={{ width: 450, height: 450, background: "#ccff00", top: "-25%", left: "30%", opacity: 0.08 }} />
        <div className="deck-kicker">02 / The Product</div>
        <h2 className="deck-h2">
          One studio. <span className="deck-gradient-text">Every tool</span> the AI musician needs.
        </h2>
        <p className="deck-sub" style={{ marginBottom: "2.5rem" }}>
          MANNIS Pro turns any idea — a link, a vibe, a lyric — into professional, platform-ready music assets.
        </p>
        <div className="deck-grid-3">
          {[
            ["🧬", "Analyze", "Drop any song link. Get a full Producer Report: genre, BPM, key, mood — and a ready-to-use style prompt."],
            ["✍️", "Create", "Generate style prompts, lyrics, rhyme schemes, and flows in 32 world languages — tuned for Suno & Udio."],
            ["🎛️", "Produce", "Generate full songs on Suno v5.5, export 10-stem MIDI packs, broadcast-ready mastered WAVs, and AI vocals — straight into your DAW."],
          ].map(([icon, title, body], i) => (
            <div className="deck-card" key={i}>
              <div className="deck-card-accent" style={{ background: ["#ccff00", "#06b6d4", "#ff0066"][i] }} />
              <span className="deck-icon">{icon}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "fusion",
    render: () => (
      <>
        <div className="deck-glow-orb" style={{ width: 480, height: 480, background: "#ff0066", top: "-22%", right: "-6%", opacity: 0.14 }} />
        <div className="deck-kicker">03 / The Moat</div>
        <h2 className="deck-h2">
          DNA Fusion Workbench: <span className="deck-gradient-text">music no one has heard before</span>
        </h2>
        <p className="deck-sub" style={{ marginBottom: "2.5rem" }}>
          Our proprietary genre-genome engine splices the chords of one genre with the rhythm and vocal DNA of another —
          then generates the record on Suno v5.5 with broadcast-ready mastering.
        </p>
        <div className="deck-grid-3">
          {[
            ["🧬", "166 Genre Genomes", "Every genre mapped as musical DNA: six-part chord maps, rhythm signatures, vocal harmony profiles, and BPM ranges — from Gospel to Gqom to Neuro DnB."],
            ["⚗️", "27,000+ Combinations", "Any two genomes fuse on demand — Salsa × Neuro DnB, Bossa Nova × UK Drill — plus 30 curated experimental blends ready to play."],
            ["🛡️", "Harmonic Uniqueness Badge", "Every generation is scored for originality with copyright insights, so creators ship music that is verifiably theirs."],
          ].map(([icon, title, body], i) => (
            <div className="deck-card" key={i}>
              <div className="deck-card-accent" style={{ background: ["#ff0066", "#ccff00", "#06b6d4"][i] }} />
              <span className="deck-icon">{icon}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "features",
    render: () => (
      <>
        <div className="deck-kicker">04 / Key Features</div>
        <h2 className="deck-h2">
          The deepest <span className="deck-volt-text">music-AI toolkit</span> on the market
        </h2>
        <div className="deck-grid-4" style={{ gap: "1rem" }}>
          {[
            ["📚", "70K+ Style Prompt Library", "70,866 curated prompts across 60+ genres — the largest prompt database for AI music."],
            ["🎶", "Suno v5.5 Engine", "1-Click Create: full songs, beats, and auto-pipeline generation with broadcast-ready club mastering on every WAV."],
            ["🎤", "Rhyme & Flow Engine", "18 rhyme schemes, 20 flow patterns, 8–64 bars, 32 world languages — from Hindi & Punjabi to Patois & Creole."],
            ["🥁", "10-Stem MIDI Generator", "GM-standard, humanized MIDI: drums, 808s, chords, pads, leads — plus studio-processed WAV stem renders."],
            ["🌍", "Vocal Dashboard", "AI singing vocals across 7 world regions with lyrics in 32 world languages — from Atlanta trap to Seoul pop to Punjabi bhangra."],
            ["🔗", "Music Link Analyzer", "Paste any track link → instant Producer Report with genre, BPM, key, mood, and matching prompts."],
            ["🏪", "DNA Marketplace", "700+ shareable MANNIS profiles — a community economy of sound."],
            ["🧩", "Chrome Extension + Discord Bot", "Generate inside Suno's own tab, from Discord, and store your whole catalog in My Uploads."],
          ].map(([icon, title, body], i) => (
            <div className="deck-card" key={i} style={{ padding: "1.2rem 1.3rem" }}>
              <span className="deck-icon" style={{ fontSize: "1.2rem", marginBottom: "0.4rem" }}>{icon}</span>
              <h3 style={{ fontSize: "0.92rem" }}>{title}</h3>
              <p style={{ fontSize: "0.78rem" }}>{body}</p>
            </div>
          ))}
        </div>
      </>
    ),
  },
  {
    id: "tech",
    render: () => (
      <>
        <div className="deck-kicker">05 / Tech Stack</div>
        <h2 className="deck-h2">
          Production-grade <span className="deck-cyan-text">engineering</span>
        </h2>
        <p className="deck-sub" style={{ marginBottom: "2.5rem" }}>
          A modern, scalable stack fusing best-in-class AI models with real audio engineering.
        </p>
        <div style={{ maxWidth: 900 }}>
          <div style={{ marginBottom: "1.75rem" }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem", letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)", marginBottom: "0.75rem", textTransform: "uppercase" }}>Platform</div>
            {["React 19", "FastAPI", "MongoDB + GridFS", "Stripe + PayPal"].map((t) => (
              <span className="deck-tech-pill" key={t}><span className="dot" style={{ background: "#06b6d4" }} />{t}</span>
            ))}
          </div>
          <div style={{ marginBottom: "1.75rem" }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem", letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)", marginBottom: "0.75rem", textTransform: "uppercase" }}>AI Models</div>
            {["OpenAI GPT", "Suno v5.5 · Kie.ai", "Replicate · MiniMax · YuE"].map((t) => (
              <span className="deck-tech-pill" key={t}><span className="dot" style={{ background: "#7c3aed" }} />{t}</span>
            ))}
          </div>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem", letterSpacing: "0.2em", color: "rgba(255,255,255,0.4)", marginBottom: "0.75rem", textTransform: "uppercase" }}>Audio Engine</div>
            {["FluidSynth", "Pedalboard FX", "FFmpeg", "GM MIDI Type-1", "Broadcast-Ready Mastering"].map((t) => (
              <span className="deck-tech-pill" key={t}><span className="dot" style={{ background: "#ccff00" }} />{t}</span>
            ))}
          </div>
        </div>
      </>
    ),
  },
  {
    id: "monetization",
    render: () => (
      <>
        <div className="deck-kicker">06 / Monetization</div>
        <h2 className="deck-h2">
          Subscription SaaS with <span className="deck-volt-text">proven willingness to pay</span>
        </h2>
        <div className="deck-grid-4" style={{ marginTop: "1rem" }}>
          {[
            { tier: "Free Trial", price: "$0", features: ["20 prompts / mo", "5 beats / mo", "5 vocals / mo"], featured: false },
            { tier: "Student", price: "$10", features: ["Unlimited prompts", "30 beats / mo", "30 vocals / mo"], featured: false },
            { tier: "Creator Pro", price: "$19", features: ["Unlimited prompts", "50 beats / mo", "50 vocals / mo"], featured: true },
            { tier: "Pro", price: "$39", features: ["Unlimited prompts", "150 beats / mo", "150 vocals / mo"], featured: false },
          ].map((p) => (
            <div className={`deck-price-card${p.featured ? " featured" : ""}`} key={p.tier}>
              {p.featured && <div className="deck-badge">Most Popular</div>}
              <div className="deck-price-tier">{p.tier}</div>
              <div className="deck-price-amount">{p.price}<span>/mo</span></div>
              <ul className="deck-price-features">
                {p.features.map((f) => <li key={f}>✓ {f}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <p className="deck-sub" style={{ marginTop: "2rem", fontSize: "0.95rem" }}>
          + Marketplace economy, live Stripe & PayPal payments, and expansion paths into API licensing and label partnerships.
        </p>
      </>
    ),
  },
  {
    id: "traction",
    render: () => (
      <>
        <div className="deck-glow-orb" style={{ width: 400, height: 400, background: "#ff0066", bottom: "-15%", right: "-5%", opacity: 0.15 }} />
        <div className="deck-kicker">07 / Traction &amp; The Ask</div>
        <h2 className="deck-h2">
          Live product. <span className="deck-pink-text">Real usage.</span> Paying customers.
        </h2>
        <div className="deck-grid-4" style={{ marginBottom: "2.5rem" }}>
          {[
            ["70,866", "Style prompts in library"],
            ["166", "Fusion genre genomes"],
            ["5,000+", "AI voice generations"],
            ["4", "Revenue tiers — live"],
          ].map(([num, label]) => (
            <div className="deck-card" key={label} style={{ textAlign: "left" }}>
              <div className="deck-stat-number">{num}</div>
              <div className="deck-stat-label">{label}</div>
            </div>
          ))}
        </div>
        <div className="deck-card" style={{ maxWidth: 860, borderColor: "rgba(255,0,102,0.4)" }}>
          <h3 style={{ fontSize: "1.2rem" }}>The Ask</h3>
          <p style={{ fontSize: "1rem", lineHeight: 1.7 }}>
            We're raising to scale marketing, expand the AI model pipeline, and capture the fastest-growing creator
            segment in music. The generative music market is projected to be a <strong style={{ color: "#ccff00" }}>multi-billion dollar category</strong> —
            and MANNIS Pro is the tooling layer every creator will need.
          </p>
        </div>
      </>
    ),
  },
  {
    id: "dashboard",
    render: () => <MissionControlSlide />,
  },
  {
    id: "madewith",
    render: ({ isExport }) => (
      <>
        <div className="deck-glow-orb" style={{ width: 450, height: 450, background: "#ff0066", top: "-18%", left: "-8%", opacity: 0.14 }} />
        <div className="deck-kicker">09 / Made With MANNIS</div>
        <h2 className="deck-h2">
          Hear it for yourself: <span className="deck-gradient-text">a record born here</span>
        </h2>
        <div className="deck-card" style={{ maxWidth: 860, padding: "1.25rem" }}>
          <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", background: "#000" }}>
            {isExport ? (
              <img
                src="https://img.youtube.com/vi/I2webF5mrRI/maxresdefault.jpg"
                alt="Beautiful Nightmares by Charles M. Leshore"
                crossOrigin="anonymous"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <iframe
                src="https://www.youtube.com/embed/I2webF5mrRI"
                title="Beautiful Nightmares by Charles M. Leshore"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="deck-madewith-video"
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
              />
            )}
          </div>
          <p
            data-testid="deck-madewith-caption"
            style={{ marginTop: "1rem", marginBottom: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", letterSpacing: "0.05em", color: "rgba(255,255,255,0.65)" }}
          >
            <span style={{ color: "#ccff00" }}>▶</span> <strong style={{ color: "#f4f4f5" }}>Beautiful Nightmares</strong> by Charles M. Leshore, produced with MANNIS
          </p>
        </div>
      </>
    ),
  },
  {
    id: "cta",
    render: ({ onDownloadPdf, isExporting }) => (
      <>
        <div className="deck-glow-orb" style={{ width: 600, height: 600, background: "#7c3aed", top: "-20%", left: "25%" }} />
        <div style={{ textAlign: "center", position: "relative" }}>
          <div className="deck-kicker" style={{ marginBottom: "2rem" }}>10 / Join Us</div>
          <h1 className="deck-h1" style={{ marginBottom: "1.5rem" }}>
            Let's build the future<br />of <span className="deck-gradient-text">music creation</span>
          </h1>
          <p className="deck-sub" style={{ margin: "0 auto 3rem auto", textAlign: "center" }}>
            Try the live product today, or reach out to talk partnership and investment.
          </p>
          <div style={{ display: "flex", gap: "1.25rem", justifyContent: "center", flexWrap: "wrap" }}>
            <a className="deck-cta-btn" href="https://sounddna.pro" target="_blank" rel="noreferrer" data-testid="deck-visit-site-btn">
              Visit sounddna.pro →
            </a>
            {onDownloadPdf && (
              <button className="deck-pdf-btn" onClick={onDownloadPdf} disabled={isExporting} data-testid="deck-download-pdf-btn">
                {isExporting ? "Exporting…" : "⬇ Download PDF"}
              </button>
            )}
          </div>
          <div style={{ marginTop: "4rem", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", color: "rgba(255,255,255,0.4)" }}>
            MANNIS PRO © 2026 —{" "}
            <a
              href="https://sounddna.pro"
              target="_blank"
              rel="noreferrer"
              data-testid="deck-footer-site-link"
              style={{ color: "#ccff00", textDecoration: "underline", textUnderlineOffset: "4px" }}
            >
              sounddna.pro
            </a>
          </div>
        </div>
      </>
    ),
  },
];

export default function Presentation() {
  const [current, setCurrent] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const exportRef = useRef(null);

  const next = useCallback(() => setCurrent((c) => Math.min(SLIDES.length - 1, c + 1)), []);
  const prev = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") next();
      if (e.key === "ArrowLeft" || e.key === "PageUp") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  const downloadPdf = async () => {
    setIsExporting(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const stage = exportRef.current;
      const slides = stage.querySelectorAll(".deck-slide");
      const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: [1600, 900] });
      for (let i = 0; i < slides.length; i++) {
        const canvas = await html2canvas(slides[i], {
          width: 1600,
          height: 900,
          scale: 1,
          backgroundColor: "#0a0a0f",
          useCORS: true,
          logging: false,
        });
        if (i > 0) pdf.addPage([1600, 900], "landscape");
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, 1600, 900);
      }
      pdf.save("SoundDNA-Pro-Investor-Deck.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("PDF export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="deck-root deck-grain" data-testid="presentation-deck">
      <div className="deck-logo-corner">SOUND <span>DNA</span> PRO</div>
      <div className="deck-counter" data-testid="deck-slide-counter">
        {String(current + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
      </div>

      {SLIDES.map((slide, i) => (
        <section
          key={slide.id}
          className={`deck-slide${i === current ? " active" : ""}`}
          data-testid={`deck-slide-${slide.id}`}
        >
          {slide.render({ onDownloadPdf: downloadPdf, isExporting })}
        </section>
      ))}

      <div className="deck-nav">
        <button className="deck-arrow" onClick={prev} disabled={current === 0} data-testid="deck-prev-btn" aria-label="Previous slide">←</button>
        <div className="deck-dots">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              className={`deck-dot${i === current ? " active" : ""}`}
              onClick={() => setCurrent(i)}
              data-testid={`deck-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
        <button className="deck-arrow" onClick={next} disabled={current === SLIDES.length - 1} data-testid="deck-next-btn" aria-label="Next slide">→</button>
      </div>

      {/* Hidden fixed-size stage used for pixel-perfect PDF export */}
      <div className="deck-export-stage" ref={exportRef} aria-hidden="true">
        {SLIDES.map((slide) => (
          <section key={`export-${slide.id}`} className="deck-slide">
            {slide.render({ onDownloadPdf: null, isExporting: false, isExport: true })}
          </section>
        ))}
      </div>
    </div>
  );
}
